import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, Circle, Clock, AlertCircle, ChevronDown, ChevronUp, Bell, Users, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { GameFormData } from '@/types/unifiedGameForm';

interface StepInfo {
  id: string;
  stepNumber: number;
  label: string;
  description: string;
  completed: boolean;
  sectionId: string;
  incompleteUsers?: { id: string; name: string; type: string }[];
}

interface RoleProgressStepperProps {
  userRole: string;
  formData: GameFormData;
  gameData: any;
  minimumPlayersRequired: number;
  readyPlayersCount: { home: number; away: number };
  onScrollToSection: (sectionId: string) => void;
}

const getRoleSteps = (
  userRole: string,
  formData: GameFormData,
  gameData: any,
  minimumPlayersRequired: number,
  readyPlayersCount: { home: number; away: number }
): StepInfo[] => {
  switch (userRole) {
    case 'venue':
      return [
        {
          id: 'venue-details',
          stepNumber: 1,
          label: 'Set Venue & Game Details',
          description: 'Action: Scroll down to the "Venue & Game Details" section. Fill in the game name, date, time, and venue address, then click "Save".',
          completed: formData.formSectionsCompleted?.venue || false,
          sectionId: 'venue',
        },
        {
          id: 'venue-teams',
          stepNumber: 2,
          label: 'Confirm Teams',
          description: 'Action: Scroll to "Team Management" and verify both the Home and Away teams are correctly assigned.',
          completed: formData.formSectionsCompleted?.teams || false,
          sectionId: 'team_owner',
        },
        {
          id: 'venue-approval',
          stepNumber: 3,
          label: 'Final Approval',
          description: 'Action: Once all other sections are complete, scroll to "Approvals" at the bottom and click "Approve" to finalize the game.',
          completed: formData.venueApproved,
          sectionId: 'approval',
        },
      ];

    case 'team_owner':
      return [
        {
          id: 'owner-roster',
          stepNumber: 1,
          label: 'Set Team Roster',
          description: 'Action: Scroll to "Team Management". Add players to your roster and assign a coach using the selection controls, then click "Save".',
          completed: formData.formSectionsCompleted?.teams || false,
          sectionId: 'team_owner',
        },
        {
          id: 'owner-review',
          stepNumber: 2,
          label: 'Review Player Readiness',
          description: `Action: Check that at least ${minimumPlayersRequired} players are marked "Ready to Play" in the "Coach Management" section below.`,
          completed: readyPlayersCount.home >= minimumPlayersRequired || readyPlayersCount.away >= minimumPlayersRequired,
          sectionId: 'coach',
        },
      ];

    case 'coach':
      return [
        {
          id: 'coach-players',
          stepNumber: 1,
          label: 'Review Roster & Player Attendance',
          description: 'Action: Scroll to "Coach Management". Review which players have confirmed attendance. Players must mark themselves as "Attending" before you can set their readiness.',
          completed: (() => {
            // Consider complete if there are roster players assigned to this game
            const hasRoster = (gameData?.home_team_id || gameData?.away_team_id) ? true : false;
            return hasRoster && (formData.formSectionsCompleted?.teams || formData.formSectionsCompleted?.players || false);
          })(),
          sectionId: 'coach',
        },
        {
          id: 'coach-readiness',
          stepNumber: 2,
          label: 'Set Player Readiness Status',
          description: `Action: In "Coach Management", use the status dropdown next to each attending player to mark them as "Ready to Play", "Injured", or "No Show". At least ${minimumPlayersRequired} must be "Ready to Play".`,
          completed: readyPlayersCount.home >= minimumPlayersRequired || readyPlayersCount.away >= minimumPlayersRequired,
          sectionId: 'coach',
        },
        {
          id: 'coach-lineup',
          stepNumber: 3,
          label: 'Set Starting Lineup',
          description: `Action: In "Coach Management", select at least ${minimumPlayersRequired} "Ready" players as starters for the game lineup.`,
          completed: (() => {
            const homeLineup = gameData?.home_starting_lineup?.length || 0;
            const awayLineup = gameData?.away_starting_lineup?.length || 0;
            return homeLineup >= minimumPlayersRequired || awayLineup >= minimumPlayersRequired;
          })(),
          sectionId: 'coach',
        },
        {
          id: 'coach-approval',
          stepNumber: 4,
          label: 'Submit Coach Approval',
          description: 'Action: Once all players are ready and lineup is set, scroll to "Approvals" at the bottom and click "Approve" to confirm your team is game-ready.',
          completed: formData.coachApproved,
          sectionId: 'approval',
        },
      ];

    case 'player':
      return [
        {
          id: 'player-attendance',
          stepNumber: 1,
          label: 'Confirm Attendance',
          description: 'Action: Scroll to "Player Section" and select "Attending" or "Not Attending" to let your coach know your availability.',
          completed: formData.formSectionsCompleted?.players || false,
          sectionId: 'player',
        },
        {
          id: 'player-approval',
          stepNumber: 2,
          label: 'Player Acknowledgment',
          description: 'Action: Scroll to "Approvals" and click "Confirm" to acknowledge you are ready to participate in this game.',
          completed: formData.playerApproved,
          sectionId: 'approval',
        },
      ];

    case 'scorekeeper':
      return [
        {
          id: 'sk-setup',
          stepNumber: 1,
          label: 'Scoring Setup',
          description: 'Action: Scroll to "Scorekeeper" section. Review the game settings and confirm the scorecard is ready.',
          completed: formData.formSectionsCompleted?.scoring || false,
          sectionId: 'scorekeeper',
        },
      ];

    case 'admin':
      return [
        {
          id: 'admin-venue',
          stepNumber: 1,
          label: 'Venue & Details',
          description: 'Action: Verify the game name, date, time, and venue are filled in under "Venue & Game Details".',
          completed: formData.formSectionsCompleted?.venue || false,
          sectionId: 'venue',
        },
        {
          id: 'admin-teams',
          stepNumber: 2,
          label: 'Teams & Rosters',
          description: 'Action: Confirm both teams have been assigned and rosters are populated in "Team Management".',
          completed: formData.formSectionsCompleted?.teams || false,
          sectionId: 'team_owner',
        },
        {
          id: 'admin-players',
          stepNumber: 3,
          label: 'Player Readiness',
          description: `Action: Ensure at least ${minimumPlayersRequired} players per team are marked "Ready to Play" in "Coach Management".`,
          completed: formData.formSectionsCompleted?.players || false,
          sectionId: 'coach',
        },
        {
          id: 'admin-approval',
          stepNumber: 4,
          label: 'Final Approval',
          description: 'Action: Scroll to "Approvals" and submit the final game approval.',
          completed: formData.gameFormStatus === 'approved',
          sectionId: 'approval',
        },
      ];

    default:
      return [];
  }
};

export const RoleProgressStepper: React.FC<RoleProgressStepperProps> = ({
  userRole,
  formData,
  gameData,
  minimumPlayersRequired,
  readyPlayersCount,
  onScrollToSection,
}) => {
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const [nudgingUser, setNudgingUser] = useState<string | null>(null);

  const steps = getRoleSteps(userRole, formData, gameData, minimumPlayersRequired, readyPlayersCount);
  const completedCount = steps.filter(s => s.completed).length;
  const totalSteps = steps.length;
  const progressPercent = totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0;
  const allComplete = completedCount === totalSteps;

  const getOverallStatus = () => {
    if (allComplete) {
      // Check if others still need to complete
      const othersComplete =
        formData.playerApproved &&
        formData.coachApproved &&
        formData.venueApproved;
      if (othersComplete) return 'completed';
      return 'awaiting_others';
    }
    return 'in_progress';
  };

  const overallStatus = getOverallStatus();

  const handleNudge = async (userId: string, userName: string) => {
    setNudgingUser(userId);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to send nudges');
        return;
      }

      await supabase.from('in_app_notifications').insert({
        user_id: userId,
        user_type: 'supabase',
        notification_type: 'game_form_nudge',
        title: 'Game Form Reminder',
        message: `You have unfinished sections for the game form "${gameData?.game_name || 'Upcoming Game'}". Please click here to complete.`,
        related_entity_id: gameData?.id,
        related_entity_type: 'game',
      });

      toast.success(`Nudge Successful — Notification to complete game form section has been sent to ${userName}.`);
    } catch (error) {
      console.error('Error sending nudge:', error);
      toast.error('Failed to send nudge notification');
    } finally {
      setNudgingUser(null);
    }
  };

  if (steps.length === 0) return null;

  return (
    <Card className="border-border">
      <CardHeader className="px-4 py-4 md:px-6 md:py-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Your Progress
              <Badge
                variant={overallStatus === 'completed' ? 'default' : overallStatus === 'awaiting_others' ? 'secondary' : 'outline'}
                className="ml-2"
              >
                {overallStatus === 'completed' && '✅ Completed'}
                {overallStatus === 'awaiting_others' && '⏳ Awaiting Others'}
                {overallStatus === 'in_progress' && `${completedCount}/${totalSteps} Steps Done`}
              </Badge>
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {minimumPlayersRequired > 0 && (
                <span>Game format requires {minimumPlayersRequired} ready players per team. </span>
              )}
              {overallStatus === 'awaiting_others' && 'Your sections are complete — waiting for other participants.'}
            </p>
          </div>
          <div className="text-right text-sm font-medium text-muted-foreground">
            {Math.round(progressPercent)}% complete
          </div>
        </div>
        <Progress value={progressPercent} className="mt-3 h-2" />
      </CardHeader>
      <CardContent className="px-4 pb-4 md:px-6 md:pb-5 pt-0">
        <div className="space-y-2">
          {steps.map((step) => {
            const isExpanded = expandedStep === step.id;
            const hasIncompleteUsers = step.incompleteUsers && step.incompleteUsers.length > 0;

            return (
              <div key={step.id} className="border border-border rounded-lg overflow-hidden">
                <button
                  onClick={() => {
                    if (step.completed) {
                      // Completed steps just scroll
                      onScrollToSection(step.sectionId);
                    } else if (hasIncompleteUsers) {
                      setExpandedStep(isExpanded ? null : step.id);
                    } else {
                      onScrollToSection(step.sectionId);
                    }
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors"
                >
                  {/* Step number / icon */}
                  <div className="flex-shrink-0">
                    {step.completed ? (
                      <CheckCircle className="h-6 w-6 text-green-500" />
                    ) : (
                      <div className="h-6 w-6 rounded-full border-2 border-primary flex items-center justify-center text-xs font-bold text-primary">
                        {step.stepNumber}
                      </div>
                    )}
                  </div>

                  {/* Label */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${step.completed ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                      {step.label}
                    </p>
                    <p className="text-xs text-muted-foreground">{step.description}</p>
                    {!step.completed && (
                      <p className="text-xs text-primary mt-0.5 font-medium">↓ Click to jump to this section</p>
                    )}
                  </div>

                  {/* Right side indicators */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {step.completed ? (
                      <Badge variant="outline" className="text-green-600 border-green-300 text-xs">Done</Badge>
                    ) : (
                      <Badge variant="outline" className="text-yellow-600 border-yellow-300 text-xs">
                        <Clock className="h-3 w-3 mr-1" />
                        Pending
                      </Badge>
                    )}
                    {hasIncompleteUsers && !step.completed && (
                      isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </button>

                {/* Expanded: show incomplete users + nudge */}
                {isExpanded && hasIncompleteUsers && (
                  <div className="px-4 pb-3 border-t border-border bg-muted/30">
                    <p className="text-xs font-medium text-muted-foreground py-2">
                      <AlertCircle className="h-3 w-3 inline mr-1" />
                      Still needs to complete:
                    </p>
                    <div className="space-y-2">
                      {step.incompleteUsers!.map((u) => (
                        <div key={u.id} className="flex items-center justify-between">
                          <span className="text-sm">{u.name}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            disabled={nudgingUser === u.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNudge(u.id, u.name);
                            }}
                          >
                            {nudgingUser === u.id ? (
                              <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            ) : (
                              <Bell className="h-3 w-3 mr-1" />
                            )}
                            Nudge
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Overall status message */}
        {overallStatus === 'awaiting_others' && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-md">
            <p className="text-sm text-blue-800 dark:text-blue-200 font-medium">
              ✅ You're all done! Waiting for other participants to complete their sections.
            </p>
          </div>
        )}
        {overallStatus === 'completed' && (
          <div className="mt-4 p-3 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-md">
            <p className="text-sm text-green-800 dark:text-green-200 font-medium">
              🎉 Game form is fully completed! All participants have finished their sections.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
